import React, { useState, useEffect, useCallback } from "react";
import api from "../../utils/api";
import { auth } from "../../firebase";
import { onAuthStateChanged } from "firebase/auth";
import {
  RefreshCw, Plug, CheckCircle, XCircle, AlertCircle,
  ChevronDown, ChevronUp, Save, Trash2, Database, Activity
} from "lucide-react";

const STATUS_CFG = {
  synced:      { label: "Synced",      cls: "bg-emerald-100 text-emerald-700" },
  not_synced:  { label: "Not Synced",  cls: "bg-slate-100 text-slate-500" },
  failed:      { label: "Failed",      cls: "bg-red-100 text-red-700" },
};

const LOG_ICON = {
  success: <CheckCircle size={14} className="text-emerald-500" />,
  failed:  <XCircle size={14} className="text-red-500" />,
};

function Badge({ status }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.not_synced;
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${cfg.cls}`}>{cfg.label}</span>;
}

export default function ERPSync() {
  const [companies, setCompanies] = useState([]);
  const [selected, setSelected] = useState("");
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [connForm, setConnForm] = useState({ siteUrl: "", apiKey: "", apiSecret: "" });
  const [showConnForm, setShowConnForm] = useState(false);
  const [connection, setConnection] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const flash = (msg, type = "success") => {
    if (type === "success") { setSuccess(msg); setTimeout(() => setSuccess(""), 4000); }
    else { setError(msg); setTimeout(() => setError(""), 5000); }
  };

  const loadCompanies = useCallback(async () => {
    try {
      const res = await api.get("/api/admin/companies");
      setCompanies(res.data.companies || []);
    } catch {}
  }, []);

  const loadStatus = useCallback(async (compId) => {
    if (!compId) return;
    try {
      const [statusRes, connRes, logsRes] = await Promise.all([
        api.get(`/api/erp/status/${compId}`),
        api.get(`/api/erp/connection/${compId}`),
        api.get(`/api/erp/logs/${compId}`),
      ]);
      setStatus(statusRes.data);
      setConnection(connRes.data.connection);
      setLogs(logsRes.data.logs || []);
      if (connRes.data.connection) {
        setConnForm(f => ({ ...f, siteUrl: connRes.data.connection.siteUrl }));
      }
    } catch (e) {
      flash(e.response?.data?.error || e.message, "error");
    }
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) loadCompanies();
    });
    return unsub;
  }, [loadCompanies]);

  useEffect(() => {
    if (selected) loadStatus(selected);
  }, [selected, loadStatus]);

  const handleSaveConnection = async () => {
    setLoading(true);
    try {
      await api.post("/api/erp/connection", { companyId: selected, ...connForm });
      flash("ERP connection saved. ERPNext sync is now enabled for this company.");
      setShowConnForm(false);
      loadStatus(selected);
    } catch (e) {
      flash(e.response?.data?.error || e.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConnection = async () => {
    if (!window.confirm("Remove ERPNext connection for this company? Sync will be disabled.")) return;
    try {
      await api.delete(`/api/erp/connection/${selected}`);
      flash("Connection removed. ERP sync disabled.");
      setConnection(null);
      loadStatus(selected);
    } catch (e) {
      flash(e.response?.data?.error || e.message, "error");
    }
  };

  const doSync = async (endpoint, label) => {
    setSyncing(s => ({ ...s, [label]: true }));
    try {
      await api.post(endpoint);
      flash(`${label} synced to ERPNext.`);
      loadStatus(selected);
    } catch (e) {
      flash(e.response?.data?.error || e.message, "error");
    } finally {
      setSyncing(s => ({ ...s, [label]: false }));
    }
  };

  return (
    <div className="max-w-5xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <div className="p-3 bg-violet-100 rounded-xl"><Database size={24} className="text-violet-600" /></div>
        <div>
          <h1 className="text-2xl font-bold text-navy-900">ERP Sync</h1>
          <p className="text-slate-500 text-sm">TinyGlobal → ERPNext alignment layer · Firestore is always the source of truth</p>
        </div>
      </div>

      {/* Alerts */}
      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-2"><AlertCircle size={16}/>{error}</div>}
      {success && <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 text-sm flex items-center gap-2"><CheckCircle size={16}/>{success}</div>}

      {/* Company Selector */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
        <label className="text-xs font-bold text-slate-500 uppercase tracking-widest block mb-2">Select Company</label>
        <select
          value={selected}
          onChange={e => setSelected(e.target.value)}
          className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-violet-300"
        >
          <option value="">— Choose a company —</option>
          {companies.map(c => <option key={c.id} value={c.id}>{c.name || c.id}</option>)}
        </select>
      </div>

      {selected && status && (
        <>
          {/* Status Overview */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[
              { label: "ERP Enabled",    value: status.erpEnabled ? "Yes" : "No", icon: <Plug size={18} className={status.erpEnabled ? "text-emerald-500" : "text-slate-400"} /> },
              { label: "Sync Status",    value: <Badge status={status.syncStatus} /> },
              { label: "Synced Records", value: status.summary?.success || 0, icon: <CheckCircle size={18} className="text-emerald-500" /> },
              { label: "Failed Records", value: status.summary?.failed || 0, icon: <XCircle size={18} className="text-red-400" /> },
            ].map((m, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{m.label}</span>
                  {m.icon}
                </div>
                <div className="text-xl font-bold text-navy-900">{m.value}</div>
              </div>
            ))}
          </div>

          {/* Connection Config */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-navy-900 flex items-center gap-2"><Plug size={18}/>ERPNext Connection</h3>
              <div className="flex gap-2">
                {connection && (
                  <button onClick={handleDeleteConnection} className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1 font-semibold">
                    <Trash2 size={13}/> Remove
                  </button>
                )}
                <button
                  onClick={() => setShowConnForm(f => !f)}
                  className="flex items-center gap-1 text-xs font-bold text-violet-600 hover:text-violet-800"
                >
                  {showConnForm ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
                  {connection ? "Edit" : "Configure"}
                </button>
              </div>
            </div>

            {connection && !showConnForm && (
              <div className="text-sm text-slate-600 flex items-center gap-2">
                <CheckCircle size={15} className="text-emerald-500"/>
                Connected to <span className="font-mono font-semibold">{connection.siteUrl}</span>
              </div>
            )}

            {!connection && !showConnForm && (
              <p className="text-sm text-slate-400">No ERPNext connection configured. Click Configure to add one.</p>
            )}

            {showConnForm && (
              <div className="space-y-3 mt-2">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">ERPNext Site URL</label>
                  <input
                    value={connForm.siteUrl}
                    onChange={e => setConnForm(f => ({ ...f, siteUrl: e.target.value }))}
                    placeholder="https://erp.yourcompany.com"
                    className="mt-1 w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">API Key</label>
                    <input
                      value={connForm.apiKey}
                      onChange={e => setConnForm(f => ({ ...f, apiKey: e.target.value }))}
                      placeholder="API Key"
                      className="mt-1 w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">API Secret</label>
                    <input
                      type="password"
                      value={connForm.apiSecret}
                      onChange={e => setConnForm(f => ({ ...f, apiSecret: e.target.value }))}
                      placeholder="API Secret"
                      className="mt-1 w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
                    />
                  </div>
                </div>
                <p className="text-xs text-slate-400">Credentials are stored securely and never exposed to the client.</p>
                <button
                  onClick={handleSaveConnection}
                  disabled={loading}
                  className="flex items-center gap-2 px-5 py-2.5 bg-navy-900 text-white rounded-xl text-sm font-bold hover:bg-navy-800 transition-colors disabled:opacity-50"
                >
                  <Save size={15}/>{loading ? "Saving..." : "Save Connection"}
                </button>
              </div>
            )}
          </div>

          {/* Manual Sync Actions */}
          {status.erpEnabled && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
              <h3 className="font-bold text-navy-900 flex items-center gap-2 mb-4"><RefreshCw size={18}/>Manual Sync</h3>
              <p className="text-xs text-slate-400 mb-4">Sync is manual only. Press to push data from Firestore → ERPNext.</p>
              <div className="flex flex-wrap gap-3">
                {[
                  { label: "Customer", endpoint: `/api/erp/sync/customer/${selected}` },
                ].map(({ label, endpoint }) => (
                  <button
                    key={label}
                    onClick={() => doSync(endpoint, label)}
                    disabled={syncing[label]}
                    className="flex items-center gap-2 px-5 py-2.5 bg-violet-600 text-white rounded-xl text-sm font-bold hover:bg-violet-700 transition-colors disabled:opacity-50"
                  >
                    <RefreshCw size={14} className={syncing[label] ? "animate-spin" : ""}/>
                    {syncing[label] ? `Syncing ${label}...` : `Sync ${label}`}
                  </button>
                ))}
              </div>
              {status.erpnextCustomerId && (
                <p className="mt-3 text-xs text-emerald-600 font-semibold">✓ ERPNext Customer ID: {status.erpnextCustomerId}</p>
              )}
            </div>
          )}

          {/* Sync Logs */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <h3 className="font-bold text-navy-900 flex items-center gap-2 mb-4">
              <Activity size={18}/>Sync Logs
              <span className="ml-auto text-xs text-slate-400">{logs.length} entries</span>
            </h3>
            {logs.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">No sync activity yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100">
                      <th className="text-left py-3 pr-4">Entity</th>
                      <th className="text-left py-3 pr-4">ID</th>
                      <th className="text-left py-3 pr-4">Status</th>
                      <th className="text-left py-3 pr-4">Time</th>
                      <th className="text-left py-3">Error</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {logs.map((log, i) => (
                      <tr key={i} className="hover:bg-slate-50 transition-colors">
                        <td className="py-3 pr-4 font-mono text-xs text-navy-700 uppercase">{log.entity}</td>
                        <td className="py-3 pr-4 font-mono text-xs text-slate-500 max-w-[120px] truncate">{log.entityId}</td>
                        <td className="py-3 pr-4">
                          <span className="flex items-center gap-1">
                            {LOG_ICON[log.status] || <AlertCircle size={14} className="text-amber-500"/>}
                            <span className={`text-xs font-semibold ${log.status === "success" ? "text-emerald-600" : "text-red-600"}`}>
                              {log.status}
                            </span>
                          </span>
                        </td>
                        <td className="py-3 pr-4 text-xs text-slate-400">{log.syncedAt ? new Date(log.syncedAt).toLocaleString() : "—"}</td>
                        <td className="py-3 text-xs text-red-400 max-w-[200px] truncate">{log.error || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
